/** Opaque, short-lived correlation carried from a Storefront host to Checkout. */
export const CHECKOUT_START_CORRELATION_PARAM = 'tss_checkout_start';

const CONSUMED_KEY_PREFIX = 'tss_checkout_start_consumed:';
const OPAQUE_EVENT_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

export interface CheckoutStartCorrelationLocation {
  pathname: string;
  search: string;
  hash: string;
}

export interface CheckoutStartCorrelationHistory {
  readonly state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export interface CheckoutStartCorrelationStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isOpaqueEventId(value: string | undefined): value is string {
  return value !== undefined && OPAQUE_EVENT_ID.test(value);
}

/**
 * Adds only an opaque intent ID to a checkout URL. Cart and shopper data must
 * never be carried in this parameter.
 */
export function appendCheckoutStartCorrelation(
  targetUrl: string,
  eventId: string | undefined
): string {
  if (!isOpaqueEventId(eventId)) return targetUrl;

  const absolute = /^[A-Za-z][A-Za-z\d+.-]*:\/\//.test(targetUrl);
  const protocolRelative = targetUrl.startsWith('//');
  const rootRelative = targetUrl.startsWith('/');
  const parsed = new URL(targetUrl, 'https://checkout.invalid');
  parsed.searchParams.set(CHECKOUT_START_CORRELATION_PARAM, eventId);

  if (absolute) return parsed.toString();
  if (protocolRelative) return `//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
  const relative = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return rootRelative ? relative : relative.replace(/^\//, '');
}

/**
 * Reads and immediately removes the source intent from the Checkout URL. The
 * destination origin remembers consumption when sessionStorage is available,
 * so refresh/replay cannot create a second correlated result.
 */
export function consumeCheckoutStartCorrelation(options: {
  location: CheckoutStartCorrelationLocation;
  history: CheckoutStartCorrelationHistory;
  storage?: CheckoutStartCorrelationStorage;
}): string | undefined {
  const params = new URLSearchParams(options.location.search);
  const candidate = params.get(CHECKOUT_START_CORRELATION_PARAM) ?? undefined;
  if (candidate === undefined) return undefined;

  params.delete(CHECKOUT_START_CORRELATION_PARAM);
  const remaining = params.toString();
  options.history.replaceState(
    options.history.state,
    '',
    `${options.location.pathname}${remaining ? `?${remaining}` : ''}${options.location.hash}`
  );

  if (!isOpaqueEventId(candidate)) return undefined;
  if (!options.storage) return candidate;

  const consumedKey = `${CONSUMED_KEY_PREFIX}${candidate}`;
  try {
    if (options.storage.getItem(consumedKey) === '1') return undefined;
    options.storage.setItem(consumedKey, '1');
  } catch {
    // Privacy modes may reject sessionStorage. The stripped URL still gives
    // this document a one-use handoff without breaking checkout.
  }
  return candidate;
}
